import {
  abbreviationFromState,
  type State,
} from '@contracts/order/valueNormalizers/State';
import {
  DIMENSION_FIELDS,
  WEIGHT_FIELDS,
} from '@src/common/measurements/MeasurementFields';
import { toDisplayDimensionValue } from '@src/common/measurements/MeasurementUnits';
import { toDisplayWeightValue } from '@src/common/measurements/WeightUnits';
import { Timestamp } from '@src/common/time/Timestamp';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import {
  CompanyMailingStateField,
  CompanyPhysicalStateField,
} from '@src/permitField/CompanyAddressFields';
import {
  InsuranceEffectiveDateField,
  InsuranceExperienceDateField,
} from '@src/permitField/InsuranceInformationFields';
import {
  LoadDispositionField,
  LoadIndustryTypeField,
  LoadLoadingMethod,
} from '@src/permitField/LoadInformationFields';
import {
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { TractorLicensePlateStateField } from '@src/permitField/TractorInformationFields';
import {
  TractorLicenseEffectiveDateField,
  TractorLicenseExpirationDateField,
} from '@src/permitField/TractorLicenseFields';
import { OwnerStateField } from '@src/permitField/TractorOwnerFields';
import { TrailerLicensePlateStateField } from '@src/permitField/TrailerInformationFields';
import { EffectiveDateField } from '@src/permitField/TripInformationFields';
import { usePermitMeasurement } from '@src/permits/hooks/usePermitMeasurement';
import { useTranslation } from 'react-i18next';

const STATE_FIELDS = [
  OwnerStateField.fieldName,
  CompanyPhysicalStateField.fieldName,
  CompanyMailingStateField.fieldName,
  TractorLicensePlateStateField.fieldName,
  TrailerLicensePlateStateField.fieldName,
];

const FIELDS_WITH_OPTIONS_DISPLAY_VALUE: string[] = [
  LoadIndustryTypeField.fieldName,
  LoadLoadingMethod.fieldName,
  LoadDispositionField.fieldName,
];

interface FieldLabels {
  fieldLabel: string;
  valueType?: string;
  value?: string;
  valueUnit?: string;
}

export const usePermitViewFieldLabels = ({
  permitApplication,
}: {
  permitApplication: PermitApplication;
}) => {
  const { t } = useTranslation();
  const { showInMetric } = usePermitMeasurement();

  const formatField = ({
    field,
    value,
  }: {
    field: string;
    value?: string;
  }): FieldLabels => {
    switch (field) {
      case PrimaryDeliveryTypeValueField.fieldName: {
        return deliveryTypeFieldsLabel(
          field,
          PrimaryDeliveryTypeField.fieldName,
          value,
        );
      }
      case SecondaryDeliveryTypeValueField.fieldName: {
        return deliveryTypeFieldsLabel(
          field,
          SecondaryDeliveryTypeField.fieldName,
          value,
        );
      }
      case InsuranceEffectiveDateField.fieldName:
      case InsuranceExperienceDateField.fieldName:
      case TractorLicenseEffectiveDateField.fieldName:
      case TractorLicenseExpirationDateField.fieldName: {
        return formatDateLabel(field, value);
      }
      case EffectiveDateField.fieldName: {
        return formatDateTimeLabel(field, value);
      }
    }

    const fieldLabel = t(`fields.${field}`);

    switch (value) {
      case undefined:
        return { fieldLabel };
      case 'true':
        return { fieldLabel, value: t('yes') };
      case 'false':
        return { fieldLabel, value: t('no') };
    }

    if (value.includes(',')) {
      return { fieldLabel, value: translateCommaSeparatedField(value) };
    }

    if (STATE_FIELDS.includes(field)) {
      return { fieldLabel, value: abbreviationFromState(value as State) };
    }

    if (FIELDS_WITH_OPTIONS_DISPLAY_VALUE.includes(field) && !!value) {
      return {
        fieldLabel,
        value: t(`fieldOptions.${value}`),
      };
    }

    if (DIMENSION_FIELDS.includes(field) && !isNaN(Number(value))) {
      const { value: displayValue, unit } = toDisplayDimensionValue({
        value: +value,
        showInMetric,
        t,
      });
      return {
        fieldLabel,
        value: displayValue,
        valueUnit: unit,
      };
    }

    if (WEIGHT_FIELDS.includes(field) && !isNaN(Number(value))) {
      const { value: displayValue, unit } = toDisplayWeightValue({
        value,
        showInMetric,
        t,
      });
      return {
        fieldLabel,
        value: displayValue.toFixed(),
        valueUnit: unit,
      };
    }

    return { fieldLabel, value };
  };

  const formatDateLabel = (fieldName: string, value?: string) => {
    return {
      fieldLabel: t(`fields.${fieldName}`),
      value: value
        ? Timestamp.from(value).formatUTCDate('yyyy-MM-dd')
        : undefined,
    };
  };

  const formatDateTimeLabel = (fieldName: string, value?: string) => {
    return {
      fieldLabel: t(`fields.${fieldName}`),
      value: value
        ? Timestamp.from(value).formatUTCDate('yyyy-MM-dd H:mm')
        : undefined,
    };
  };

  const deliveryTypeFieldsLabel = (
    field: string,
    fieldType: string,
    value?: string,
  ): FieldLabels => {
    const valueType = t(
      `fields.${field}.${permitApplication.getFieldValue(fieldType)?.toString()}`,
    );
    return {
      fieldLabel: t(`fields.${fieldType}`),
      valueType,
      value,
    };
  };

  const translateCommaSeparatedField = (value: string) => {
    return value
      .split(',')
      .map((text) => t(`fieldOptions.${text}`, text))
      .join(', ');
  };

  return { formatField };
};
